import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
    Button,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Select,
    Space,
    Switch,
    Table,
    Tabs,
    Tag,
    Typography,
    message
} from 'antd';
import {
    ApartmentOutlined,
    CloudDownloadOutlined,
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import Layout from './Layout';

const { Title, Text } = Typography;

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
});

const OrgStructurePage = () => {
    const [departments, setDepartments] = useState([]);
    const [positions, setPositions] = useState([]);
    const [loadingDep, setLoadingDep] = useState(false);
    const [loadingPos, setLoadingPos] = useState(false);
    const [syncingBitrix, setSyncingBitrix] = useState(false);
    const [depModal, setDepModal] = useState(false);
    const [posModal, setPosModal] = useState(false);
    const [editingDep, setEditingDep] = useState(null);
    const [editingPos, setEditingPos] = useState(null);
    const [depForm] = Form.useForm();
    const [posForm] = Form.useForm();

    const fetchDepartments = useCallback(async () => {
        setLoadingDep(true);
        try {
            const { data } = await axios.get('/api/departments', {
                headers: authHeaders()
            });
            setDepartments(data.data || []);
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    'Ошибка загрузки отделов'
            );
        } finally {
            setLoadingDep(false);
        }
    }, []);

    const fetchPositions = useCallback(async () => {
        setLoadingPos(true);
        try {
            const { data } = await axios.get('/api/positions', {
                headers: authHeaders()
            });
            setPositions(data.data || []);
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    'Ошибка загрузки должностей'
            );
        } finally {
            setLoadingPos(false);
        }
    }, []);

    useEffect(() => {
        fetchDepartments();
        fetchPositions();
    }, [fetchDepartments, fetchPositions]);

    const handleSyncBitrix = async () => {
        setSyncingBitrix(true);
        try {
            const { data } = await axios.post(
                '/api/departments/sync-bitrix',
                { overwrite: true },
                { headers: authHeaders() }
            );
            const report = data.report || {};
            const warning = report.departments?.warning;
            message.success(
                `Bitrix: отделов ${report.departments?.upserted || 0}, должностей ${report.positions?.upserted || 0}, привязок отдел/должность ${report.links?.department_set || 0}/${report.links?.position_set || 0}`
            );
            if (warning) {
                message.warning(warning, 8);
            }
            fetchDepartments();
            fetchPositions();
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    error.message ||
                    'Ошибка синхронизации Bitrix'
            );
        } finally {
            setSyncingBitrix(false);
        }
    };

    const departmentOptions = useMemo(
        () =>
            departments.map(d => ({
                value: d.id,
                label: d.name
            })),
        [departments]
    );

    const openDepModal = (row = null) => {
        setEditingDep(row);
        setDepModal(true);
        if (row) {
            depForm.setFieldsValue({
                name: row.name,
                code: row.code || '',
                description: row.description || '',
                sort_order: row.sort_order ?? 100,
                is_active: row.is_active !== false
            });
        } else {
            depForm.resetFields();
            depForm.setFieldsValue({
                sort_order: 100,
                is_active: true
            });
        }
    };

    const openPosModal = (row = null) => {
        setEditingPos(row);
        setPosModal(true);
        if (row) {
            posForm.setFieldsValue({
                name: row.name,
                code: row.code || '',
                department_id: row.department_id || null,
                description: row.description || '',
                sort_order: row.sort_order ?? 100,
                is_active: row.is_active !== false
            });
        } else {
            posForm.resetFields();
            posForm.setFieldsValue({
                sort_order: 100,
                is_active: true,
                department_id: null
            });
        }
    };

    const saveDepartment = async values => {
        try {
            const payload = {
                ...values,
                code: values.code || null,
                description: values.description || null
            };
            if (editingDep) {
                await axios.put(
                    `/api/departments/${editingDep.id}`,
                    payload,
                    { headers: authHeaders() }
                );
                message.success('Отдел обновлён');
            } else {
                await axios.post('/api/departments', payload, {
                    headers: authHeaders()
                });
                message.success('Отдел создан');
            }
            setDepModal(false);
            setEditingDep(null);
            depForm.resetFields();
            fetchDepartments();
            fetchPositions();
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    'Не удалось сохранить отдел'
            );
        }
    };

    const savePosition = async values => {
        try {
            const payload = {
                ...values,
                code: values.code || null,
                description: values.description || null,
                department_id:
                    values.department_id == null ||
                    values.department_id === ''
                        ? null
                        : values.department_id
            };
            if (editingPos) {
                await axios.put(
                    `/api/positions/${editingPos.id}`,
                    payload,
                    { headers: authHeaders() }
                );
                message.success('Должность обновлена');
            } else {
                await axios.post('/api/positions', payload, {
                    headers: authHeaders()
                });
                message.success('Должность создана');
            }
            setPosModal(false);
            setEditingPos(null);
            posForm.resetFields();
            fetchPositions();
            fetchDepartments();
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    'Не удалось сохранить должность'
            );
        }
    };

    const removeDepartment = async id => {
        try {
            await axios.delete(`/api/departments/${id}`, {
                headers: authHeaders()
            });
            message.success('Отдел удалён');
            fetchDepartments();
            fetchPositions();
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    'Не удалось удалить отдел'
            );
        }
    };

    const removePosition = async id => {
        try {
            await axios.delete(`/api/positions/${id}`, {
                headers: authHeaders()
            });
            message.success('Должность удалена');
            fetchPositions();
            fetchDepartments();
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    'Не удалось удалить должность'
            );
        }
    };

    const depColumns = [
        {
            title: 'Название',
            dataIndex: 'name',
            key: 'name'
        },
        {
            title: 'Bitrix',
            dataIndex: 'bitrix_id',
            key: 'bitrix_id',
            width: 90,
            render: id =>
                id ? <Tag color="purple">{id}</Tag> : '—'
        },
        {
            title: 'Родитель',
            key: 'parent',
            width: 160,
            render: (_, row) => row.parent?.name || '—'
        },
        {
            title: 'Код',
            dataIndex: 'code',
            key: 'code',
            width: 140,
            render: v => v || '—'
        },
        {
            title: 'Должностей',
            key: 'positions_count',
            width: 110,
            render: (_, row) => row.positions?.length || 0
        },
        {
            title: 'Порядок',
            dataIndex: 'sort_order',
            key: 'sort_order',
            width: 90
        },
        {
            title: 'Статус',
            dataIndex: 'is_active',
            key: 'is_active',
            width: 110,
            render: active =>
                active !== false ? (
                    <Tag color="green">Активен</Tag>
                ) : (
                    <Tag>Неактивен</Tag>
                )
        },
        {
            title: '',
            key: 'actions',
            width: 120,
            render: (_, row) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => openDepModal(row)}
                    />
                    <Popconfirm
                        title="Удалить отдел?"
                        onConfirm={() => removeDepartment(row.id)}
                    >
                        <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                        />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const posColumns = [
        {
            title: 'Название',
            dataIndex: 'name',
            key: 'name'
        },
        {
            title: 'Код',
            dataIndex: 'code',
            key: 'code',
            width: 140,
            render: v => v || '—'
        },
        {
            title: 'Отдел',
            key: 'department',
            width: 200,
            render: (_, row) =>
                row.department?.name || '—'
        },
        {
            title: 'Порядок',
            dataIndex: 'sort_order',
            key: 'sort_order',
            width: 90
        },
        {
            title: 'Статус',
            dataIndex: 'is_active',
            key: 'is_active',
            width: 110,
            render: active =>
                active !== false ? (
                    <Tag color="green">Активна</Tag>
                ) : (
                    <Tag>Неактивна</Tag>
                )
        },
        {
            title: '',
            key: 'actions',
            width: 120,
            render: (_, row) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => openPosModal(row)}
                    />
                    <Popconfirm
                        title="Удалить должность?"
                        onConfirm={() => removePosition(row.id)}
                    >
                        <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                        />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <Layout>
            <div style={{ padding: 24 }}>
                <Space
                    style={{
                        width: '100%',
                        justifyContent: 'space-between',
                        marginBottom: 16
                    }}
                    wrap
                >
                    <div>
                        <Title level={3} style={{ margin: 0 }}>
                            <ApartmentOutlined /> Отделы и
                            должности
                        </Title>
                        <Text type="secondary">
                            Справочники для привязки к
                            пользователям. Можно подтянуть из
                            Bitrix (отделы + должности
                            сотрудников).
                        </Text>
                    </div>
                    <Space wrap>
                        <Button
                            type="primary"
                            icon={<CloudDownloadOutlined />}
                            loading={syncingBitrix}
                            onClick={handleSyncBitrix}
                        >
                            Из Bitrix
                        </Button>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => {
                                fetchDepartments();
                                fetchPositions();
                            }}
                        >
                            Обновить
                        </Button>
                    </Space>
                </Space>

                <Tabs
                    items={[
                        {
                            key: 'departments',
                            label: 'Отделы',
                            children: (
                                <>
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        style={{
                                            marginBottom: 12
                                        }}
                                        onClick={() =>
                                            openDepModal()
                                        }
                                    >
                                        Добавить отдел
                                    </Button>
                                    <Table
                                        rowKey="id"
                                        loading={loadingDep}
                                        columns={depColumns}
                                        dataSource={departments}
                                        pagination={{
                                            pageSize: 20
                                        }}
                                        size="middle"
                                    />
                                </>
                            )
                        },
                        {
                            key: 'positions',
                            label: 'Должности',
                            children: (
                                <>
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        style={{
                                            marginBottom: 12
                                        }}
                                        onClick={() =>
                                            openPosModal()
                                        }
                                    >
                                        Добавить должность
                                    </Button>
                                    <Table
                                        rowKey="id"
                                        loading={loadingPos}
                                        columns={posColumns}
                                        dataSource={positions}
                                        pagination={{
                                            pageSize: 20
                                        }}
                                        size="middle"
                                    />
                                </>
                            )
                        }
                    ]}
                />
            </div>

            <Modal
                title={
                    editingDep
                        ? 'Редактировать отдел'
                        : 'Новый отдел'
                }
                open={depModal}
                onCancel={() => {
                    setDepModal(false);
                    setEditingDep(null);
                    depForm.resetFields();
                }}
                onOk={() => depForm.submit()}
                destroyOnClose
            >
                <Form
                    form={depForm}
                    layout="vertical"
                    onFinish={saveDepartment}
                >
                    <Form.Item
                        name="name"
                        label="Название"
                        rules={[
                            {
                                required: true,
                                message: 'Укажите название'
                            }
                        ]}
                    >
                        <Input placeholder="Например: Отдел продаж" />
                    </Form.Item>
                    <Form.Item name="code" label="Код">
                        <Input placeholder="sales (опционально)" />
                    </Form.Item>
                    <Form.Item
                        name="description"
                        label="Описание"
                    >
                        <Input.TextArea rows={2} />
                    </Form.Item>
                    <Form.Item
                        name="sort_order"
                        label="Порядок сортировки"
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                        />
                    </Form.Item>
                    <Form.Item
                        name="is_active"
                        label="Активен"
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={
                    editingPos
                        ? 'Редактировать должность'
                        : 'Новая должность'
                }
                open={posModal}
                onCancel={() => {
                    setPosModal(false);
                    setEditingPos(null);
                    posForm.resetFields();
                }}
                onOk={() => posForm.submit()}
                destroyOnClose
            >
                <Form
                    form={posForm}
                    layout="vertical"
                    onFinish={savePosition}
                >
                    <Form.Item
                        name="name"
                        label="Название"
                        rules={[
                            {
                                required: true,
                                message: 'Укажите название'
                            }
                        ]}
                    >
                        <Input placeholder="Например: Менеджер" />
                    </Form.Item>
                    <Form.Item name="code" label="Код">
                        <Input placeholder="manager (опционально)" />
                    </Form.Item>
                    <Form.Item
                        name="department_id"
                        label="Отдел"
                    >
                        <Select
                            allowClear
                            placeholder="Без привязки к отделу"
                            options={departmentOptions}
                        />
                    </Form.Item>
                    <Form.Item
                        name="description"
                        label="Описание"
                    >
                        <Input.TextArea rows={2} />
                    </Form.Item>
                    <Form.Item
                        name="sort_order"
                        label="Порядок сортировки"
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                        />
                    </Form.Item>
                    <Form.Item
                        name="is_active"
                        label="Активна"
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>
        </Layout>
    );
};

export default OrgStructurePage;
